import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getCurrentYear, getInlineLoader } from "../Helpers";
import {
  FaArrowUp,
  FaBolt,
  FaBuilding,
  FaEnvelope,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaLocationArrow,
  FaPhone,
  FaShield,
  FaSolarPanel,
  FaTwitter,
} from "react-icons/fa6";

import {IoMdCheckmarkCircle, IoMdCheckMarkCircle} from "react-icons/io";


export default function Footer() {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [email, setEmail] = useState("");

  useEffect(() => {
    setReady(true);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      alert(`Thank you for subscribing with ${email}`);
      setEmail("");
    }
  };

  if (!ready) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center">{getInlineLoader()}</div>
      </div>
    );
  }

  const navLinks = [
    { label: "Home", target: "home" },
    { label: "About Us", target: "about_us" },
    { label: "Services", target: "services" },
    { label: "Projects", target: "projects" },
    { label: "Contact", target: "contact_us" },
  ];

  const services = [
    { icon: <FaBolt />, label: "Electrical Engineering" },
    { icon: <FaBuilding />, label: "Civil Engineering" },
    { icon: <FaSolarPanel />, label: "Renewable Energy" },
    { icon: <FaShield />, label: "Project Consultancy" },
  ];

  const socialLinks = [
    { icon: <FaLinkedin />, href: "#", label: "LinkedIn" },
    { icon: <FaTwitter />, href: "#", label: "Twitter" },
    { icon: <FaFacebook />, href: "#", label: "Facebook" },
    { icon: <FaInstagram />, href: "#", label: "Instagram" },
  ];

  const certifications = [
    { label: "CRB Certified", icon: <IoMdCheckmarkCircle /> },
    { label: "TANESCO Licensed", icon: <FaShield /> },
    { label: "VAT Registered", icon: <IoMdCheckmarkCircle /> },
  ];

  const contactItems = [
    {
      icon: <FaLocationArrow />,
      label: "P.O. Box 11913, Dar es Salaam, Tanzania",
      href: "https://maps.google.com/?q=Dar+es+Salaam+Tanzania",
    },
    {
      icon: <FaPhone />,
      label: "+255 782 665 569",
      href: "tel:+255782665569",
    },
    {
      icon: <FaEnvelope />,
      label: "info@medalge.com",
      href: "mailto:info@medalge.com",
    },
  ];

  const businessHours = [
    { days: "Monday - Friday", hours: "8:00 AM - 5:00 PM" },
    { days: "Saturday", hours: "8:00 AM - 2:00 PM" },
    { days: "Sunday", hours: "Closed" },
  ];

  return (
    <>
      {/* Modern Footer */}
      <footer
        className="MainBodySection position-relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
          paddingTop: "80px",
          paddingBottom: "32px",
        }}
      >
        {/* Subtle Animated Grid Background */}
        <div
          className="position-absolute w-100 h-100 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />

        {/* Decorative Glow */}
        <div
          className="position-absolute top-0 end-0 opacity-10"
          style={{
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, #3B82F6 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />

        <div className="container position-relative px-3">
          <div className="row g-4 g-lg-5">
            {/* Brand Column */}
            <div className="col-md-4 mt-5" data-aos="fade-up">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div>
                  <h5 className="fw-bold text-white mb-0">Medal Global Engineering</h5>
                  <small className="text-white-50">Est. 2019 • Tanzania</small>
                </div>
              </div>

              {/* Contact Links */}
              <div className="d-flex flex-column gap-2.5">
                {contactItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="d-flex align-items-center gap-3 text-white-50 text-decoration-none group"
                    style={{ padding: "6px 0", transition: "color 0.2s ease" }}
                  >
                    <div className="transition-all group-hover:bg-primary group-hover:bg-opacity-20 flex-shrink-0">
                      <div className="text-primary transition-colors group-hover:text-white">
                        {item.icon}
                      </div>
                    </div>
                    <span className="small transition-colors group-hover:text-white">
                      {item.label}
                    </span>
                  </a>
                ))}
              </div>

              {/* Social Links */}
              <div className="d-flex gap-2 mt-5">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    className="social-icon d-flex align-items-center justify-content-center"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "14px",
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      color: "rgba(255,255,255,0.85)",
                      transition: "all 0.25s ease",
                      textDecoration: "none",
                    }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Column */}
            <div className="col-md-4 mt-5" data-aos="fade-up" data-aos-delay="100">
              <h6 className="text-white fw-semibold mb-4 text-uppercase tracking-wide" style={{ fontSize: "0.8rem", letterSpacing: "0.12em" }}>
                Navigation
              </h6>
              <div className="d-flex flex-column gap-1.5">
                {navLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => appContext.navTo({ item: link.target })}
                    className="nav-link text-start text-white-50 text-decoration-none bg-transparent border-0 p-0 mt-2"
                    style={{
                      padding: "8px 0",
                      fontSize: "0.95rem",
                      transition: "color 0.2s ease, transform 0.2s ease",
                      cursor: "pointer",
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Services Column */}
            <div className="col-md-4 mt-5" data-aos="fade-up" data-aos-delay="200">
              <h6 className="text-white fw-semibold mb-4 text-uppercase tracking-wide" style={{ fontSize: "0.8rem", letterSpacing: "0.12em" }}>
                Our Expertise
              </h6>
              <div className="d-flex flex-column gap-3">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="d-flex align-items-center gap-3 text-white-50"
                    style={{ padding: "4px 0" }}
                  >
                    <div className="text-primary flex-shrink-0">{service.icon}</div>
                    <span className="small">{service.label}</span>
                  </div>
                ))}
              </div>

            </div>

          </div>

          

          {/* Bottom Bar */}
          <div
            className="mt-5 pt-5"
            style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
          >
            <div className="row align-items-center g-3">
              <div className="col-md-6 text-center text-md-start">
                <p className="mb-1 text-white-50 small">
                  © Medal Global Engineering {getCurrentYear()}
                </p>
                <p className="mb-0" style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.5)" }}>
                  Engineering excellence across Tanzania & East Africa
                </p>
              </div>
              <div className="col-md-6 text-center text-md-end">
                <div className="footer-credit">
                  <span className="text-white-50 small">
                    Website by{" "}
                  </span>
                  <a
                    href="https://werenium.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none fw-medium small hover-underline"
                  >
                    Werenium Technologies
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll to Top Button */}
        <button
          onClick={scrollToTop}
          className={`position-fixed bottom-5 end-4 btn btn-primary rounded-circle shadow-lg transition-all ${
            showScrollTop ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-4 pointer-events-none"
          }`}
          style={{
            width: "50px",
            height: "50px",
            zIndex: 1000,
            backgroundColor: "#3B82F6",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-white" size={16} />
        </button>
      </footer>

      {/* Custom Styles */}
      <style>{`
        .tracking-wide { letter-spacing: 0.12em; }
        .transition-all { transition: all 0.25s ease; }
        .transition-colors { transition: color 0.2s ease; }
        .rotate-45 { transform: rotate(-45deg); }
        .translate-y-0 { transform: translateY(0); }
        .translate-y-4 { transform: translateY(16px); }
        .pointer-events-none { pointer-events: none; }
        .hover-underline { position: relative; }
        .hover-underline::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 1px;
          background: #3B82F6;
          transition: width 0.2s ease;
        }
        .hover-underline:hover::after { width: 100%; }
        
        .social-icon:hover {
          background: #3B82F6 !important;
          border-color: #3B82F6 !important;
          color: #fff !important;
          transform: translateY(-3px);
        }
        
        .nav-link:hover {
          color: #fff !important;
          transform: translateX(4px);
        }
        
        .group:hover .text-white-50 {
          color: #fff !important;
        }
        
        .group:hover .bg-white {
          background: rgba(59, 130, 246, 0.2) !important;
        }
        
        .group:hover .text-primary {
          color: #fff !important;
        }
        
        .form-control::placeholder {
          color: rgba(255,255,255,0.5) !important;
          opacity: 1;
        }
        
        .form-control:focus {
          background: rgba(255,255,255,0.15) !important;
          border-color: #3B82F6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
          color: #fff !important;
          outline: none;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        [data-aos] {
          animation: fadeIn 0.5s ease forwards;
        }
      `}</style>
    </>
  );
}